import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, SectionHeading, Accent } from "@/components/Reveal";
import { getServiceById } from "@/lib/servicePages";
import { useLang } from "@/lib/i18n";

export const ServiceIncludes = ({ page }) => {
  const { t } = useLang();
  return (
    <section data-testid="service-includes" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading kicker={t.service.includes.kicker} title={<Accent parts={t.service.includes.title} />} testid="service-includes-heading" />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
        {page.includes.map((it, i) => (
          <Reveal key={it.h} delay={i * 0.05} className="bg-ink">
            <div data-testid={`service-include-${i}`} className="group h-full p-8 hover:bg-coal/60 transition-colors duration-500">
              <p className="font-mono text-xs text-gold mb-6">0{i + 1}</p>
              <h3 className="font-serif text-2xl text-mist mb-3 leading-tight">{it.h}</h3>
              <p className="text-dim text-sm leading-relaxed">{it.p}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export const ServiceForWhom = ({ page }) => {
  const { t } = useLang();
  return (
    <section data-testid="service-for-whom" className="bg-coal/40 border-y border-line py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionHeading kicker={t.service.forWhom.kicker} title={<Accent parts={t.service.forWhom.title} />} testid="service-for-whom-heading" />
        </div>
        <ul className="lg:col-span-7 flex flex-col divide-y divide-line border-y border-line lg:mt-4">
          {page.forWhom.map((f, i) => (
            <Reveal key={f} delay={i * 0.06}>
              <li data-testid={`service-for-whom-${i}`} className="flex items-start gap-5 py-6">
                <span className="mt-1 w-6 h-6 shrink-0 border border-gold/60 text-gold flex items-center justify-center">
                  <Check size={13} />
                </span>
                <p className="text-mist text-base leading-relaxed">{f}</p>
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};

export const ServiceSteps = ({ page }) => {
  const { t } = useLang();
  return (
    <section data-testid="service-steps" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading kicker={t.service.steps.kicker} title={<Accent parts={t.service.steps.title} />} testid="service-steps-heading" />
      <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative">
        <div className="hidden lg:block absolute top-5 left-0 right-0 h-px bg-line" aria-hidden="true" />
        {page.steps.map((s, i) => (
          <Reveal key={s.h} delay={i * 0.1}>
            <li data-testid={`service-step-${i}`} className="relative">
              <span className="relative z-10 inline-flex w-10 h-10 items-center justify-center bg-ink border border-gold text-gold font-mono text-sm mb-6">{i + 1}</span>
              <h3 className="font-serif text-2xl text-mist mb-3 leading-tight">{s.h}</h3>
              <p className="text-dim text-sm leading-relaxed">{s.p}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </section>
  );
};

export const ServiceFaq = ({ page }) => {
  const { t } = useLang();
  return (
    <section data-testid="service-faq" className="bg-coal/40 border-y border-line py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <SectionHeading kicker={t.service.faq.kicker} title={<Accent parts={t.service.faq.title} />} testid="service-faq-heading" />
        <Reveal>
          <Accordion type="single" collapsible className="w-full">
            {page.faqs.map((f, i) => (
              <AccordionItem key={i} value={`sfaq-${i}`} className="border-line" data-testid={`service-faq-item-${i}`}>
                <AccordionTrigger data-testid={`service-faq-trigger-${i}`} className="font-serif text-xl md:text-2xl text-mist hover:text-gold hover:no-underline text-left py-6 transition-colors duration-300">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-dim leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

export const RelatedServices = ({ page }) => {
  const { t, lang } = useLang();
  return (
    <section data-testid="service-related" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading kicker={t.service.related.kicker} title={<Accent parts={t.service.related.title} />} testid="service-related-heading" />
      <div className="grid md:grid-cols-3 gap-6">
        {page.related.map((id) => getServiceById(id, lang)).map((r, i) => (
          <Reveal key={r.id} delay={i * 0.08}>
            <Link
              to={`/uslugi/${r.slug}`}
              data-testid={`service-related-${r.id}`}
              className="group block h-full border border-line bg-ink p-8 hover:border-gold/40 hover:-translate-y-1 transition-[transform,border-color] duration-500"
            >
              <p className="font-mono text-xs text-gold mb-6">{r.num}</p>
              <h3 className="font-serif text-2xl text-mist mb-3 leading-tight">{r.title}</h3>
              <p className="text-dim text-sm leading-relaxed mb-6">{r.intro}</p>
              <span className="inline-flex items-center gap-2 text-sm text-dim group-hover:text-gold transition-colors duration-300">
                {t.service.related.more} <ArrowUpRight size={15} />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
